/**
 * Luvè email signup and slide-in (snippets/luve-signup.liquid, blocks/luve-email-signup.liquid,
 * snippets/luve-email-capture.liquid). Words and rules: Theme settings > Luvè email signup.
 *
 * <luve-signup>        Shopify's own customer form. Picks the offer by the date in Sydney: the Christmas reminder
 *                      until the day before the order-by date, then the list (the welcome offer, when it is on, stays
 *                      all season). The tags follow the offer the shopper saw. It posts the normal way, because
 *                      Shopify's bot check (an invisible hCaptcha on every customer form of this store) needs it to:
 *                      a background post is refused (400, "Missing CAPTCHA token"), or sent to Shopify's challenge
 *                      page, which only a real page can follow. Shopify's answer comes back as the page.
 *
 * <luve-email-capture> The slide-in. It waits for real engagement (time on the page, and a scroll or a second page,
 *                      or on a computer the pointer heading for the tabs) and a quiet moment (no dialog or drawer
 *                      open, the set builder out of view, nothing typed or tapped for a few seconds). Closed, it
 *                      stays away for the snooze days; after a sign-up on this device, for good; shown and left
 *                      alone, it waits a day. Back from a sign-up made in it, it opens once more on its thanks.
 *
 * Testing: ?luve_capture=open opens the slide-in at once; ?luve_capture=reset forgets a close or a sign-up;
 * ?luve_today=2026-11-29 shows the offers as they would read on that date.
 */

const STORE_KEY = 'luve-capture';
const PAGES_KEY = 'luve-capture-pages';
const DAY_MS = 24 * 60 * 60 * 1000;
const QUIET_MS = 3000;
const EXIT_AFTER_MS = 10000;
const SUCCESS_LINGER_MS = 6000;
/** The button reads as busy while Shopify's bot check works; if it never sends (a puzzle closed), it works again */
const BUSY_MS = 15000;

const params = new URLSearchParams(window.location.search);

/** What this device remembers: { subscribedAt, dismissedAt, seenAt } in ms. Private windows may refuse storage */
const memory = {
  read() {
    try {
      return JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  },
  /** @param {Record<string, number>} patch */
  write(patch) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({ ...memory.read(), ...patch }));
    } catch {
      // Nothing to remember with; the slide-in will simply ask again another day
    }
  },
  forget() {
    try {
      window.localStorage.removeItem(STORE_KEY);
    } catch {
      // As above
    }
  },
};

// Back from a normal post (no script, or after Shopify's bot check): this device has signed up
if (params.get('customer_posted') === 'true') memory.write({ subscribedAt: Date.now() });
if (params.get('luve_capture') === 'reset') memory.forget();

const sydneyDate = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Sydney',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The element that scrolls the page. From 990px Horizon keeps the window still and scrolls `.page-wrapper` instead
 * (assets/scroll-container.js); below that the document scrolls as usual. Asked every time, so a window resized across
 * 990px is still measured right.
 */
function pageScroller() {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) {
    const overflow = getComputedStyle(wrapper).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return wrapper;
  }
  return document.scrollingElement || document.documentElement;
}

/** Today in Sydney as YYYY-MM-DD: the order-by day ends at midnight where the orders are placed */
function sydneyToday() {
  const forced = params.get('luve_today');
  if (forced && /^\d{4}-\d{2}-\d{2}$/.test(forced)) return forced;
  const parts = Object.fromEntries(sydneyDate.formatToParts(new Date()).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

class LuveSignup extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #busyTimer;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.applyOffer();
    // Shopify's answer to a post comes back as this page: its thanks or its error is already in the status line
    if (this.querySelector('[data-luve-status] [data-luve-signup-posted]')) this.dataset.state = 'done';
    else if (this.querySelector('[data-luve-status] [data-luve-signup-error]')) this.dataset.state = 'error';
    this.form.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#busyTimer);
  }

  /** The offer to show now: welcome if it is on, else the reminder until the day before the order-by date */
  chooseOffer() {
    const offers = [...this.querySelectorAll('[data-luve-variant]')].map((element) =>
      element.getAttribute('data-luve-variant')
    );
    if (offers.includes('welcome')) return 'welcome';
    const cutoff = this.dataset.cutoff || '';
    if (offers.includes('reminder') && /^\d{4}-\d{2}-\d{2}$/.test(cutoff) && sydneyToday() < cutoff) return 'reminder';
    return offers.includes('list') ? 'list' : offers[0] || 'list';
  }

  /** Shows one offer and gives the form its tags and button label */
  applyOffer() {
    const offer = this.chooseOffer();
    let active = null;
    for (const element of this.querySelectorAll('[data-luve-variant]')) {
      const current = element.getAttribute('data-luve-variant') === offer;
      /** @type {HTMLElement} */ (element).hidden = !current;
      if (current) active = /** @type {HTMLElement} */ (element);
    }
    this.dataset.variant = offer;
    if (!active) return offer;
    const tags = this.querySelector('[data-luve-tags]');
    if (tags instanceof HTMLInputElement && active.dataset.tags) tags.value = active.dataset.tags;
    const label = this.querySelector('[data-luve-button]');
    if (label && active.dataset.button) label.textContent = active.dataset.button;
    return offer;
  }

  /** The visible heading, for the slide-in's accessible name */
  get headingId() {
    return this.querySelector(`[data-luve-variant="${this.dataset.variant}"] .luve-signup__heading`)?.id || '';
  }

  /** True when the page came back from a sign-up (Shopify's thanks is in the status line) */
  get posted() {
    return this.dataset.state === 'done';
  }

  /**
   * The form posts the normal way; this only shows the press. Shopify's bot check attaches itself to the form on its
   * first focus, then fetches its token and posts. If a press comes before it has attached (its script still loading),
   * Shopify stops that press, so ask it to attach (it never attaches twice) and press again once it has.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = this.form;
    if (!form) return;
    clearTimeout(this.#busyTimer);
    this.setAttribute('aria-busy', 'true');
    this.#busyTimer = setTimeout(() => this.removeAttribute('aria-busy'), BUSY_MS);

    const captcha = window.Shopify?.captcha;
    const attached = form.dataset.hcaptchaBound || form.dataset.recaptchaBound;
    if (!captcha || attached || form.dataset.nocaptcha === 'true' || event.defaultPrevented) return;
    try {
      // Never from inside this submit: the browser ignores a new press while one is still being dispatched
      captcha.protect(form, () => setTimeout(() => form.requestSubmit(), 0));
    } catch {
      // Shopify's own focus handler attaches it; the next press goes through
    }
  };

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-luve-copy]') : null
    );
    if (!button) return;
    const code = button.getAttribute('data-luve-copy') || '';
    try {
      await navigator.clipboard.writeText(code);
      button.textContent = 'Copied';
    } catch {
      // No clipboard (an older browser or an insecure page): the code is selectable on its own
      const strong = this.querySelector('.luve-signup__code');
      if (strong) window.getSelection()?.selectAllChildren(strong);
    }
  };
}

class LuveEmailCapture extends HTMLElement {
  #visibleMs = 0;
  #lastTick = 0;
  #maxScroll = 0;
  #lastInput = -Infinity;
  #pages = 1;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #watch;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #linger;
  #opened = false;
  #listening = false;
  /** @type {HTMLElement | null} Where focus was before it came into the panel, to hand it back on close */
  #returnFocus = null;

  connectedCallback() {
    this.#pages = this.#countPage();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusin', this.#onFocusIn);

    // Back from a sign-up made here (Shopify returns to the form's own anchor): open once more, on the thanks
    if (params.get('customer_posted') === 'true' && window.location.hash === '#LuveCapture' && this.#signup?.posted) {
      this.open({ remember: false });
      this.#thank();
      return;
    }
    if (params.get('luve_capture') === 'open' || this.hasAttribute('data-editor-open')) {
      this.open({ remember: false });
      return;
    }
    if (!this.#eligible()) return;

    this.#listen(true);
    this.#lastTick = performance.now();
    this.#timer = setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    this.#listen(false);
    clearInterval(this.#timer);
    clearInterval(this.#watch);
    clearTimeout(this.#linger);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusin', this.#onFocusIn);
  }

  get #signup() {
    return /** @type {LuveSignup | null} */ (this.querySelector('luve-signup'));
  }

  get #snoozeMs() {
    return (Number(this.dataset.snooze) || 14) * DAY_MS;
  }

  /** Has this device already answered, and is there an offer worth showing today? */
  #eligible() {
    const remembered = memory.read();
    const now = Date.now();
    if (remembered.subscribedAt) return false;
    if (remembered.dismissedAt && now - remembered.dismissedAt < this.#snoozeMs) return false;
    if (remembered.seenAt && now - remembered.seenAt < DAY_MS) return false;
    const signup = this.#signup;
    const offer = typeof signup?.chooseOffer === 'function' ? signup.chooseOffer() : '';
    if (!offer) return false;
    // From the order-by date the reminder is over: keep the slide-in with the list words only if Theme settings say so
    if (offer === 'list' && !this.hasAttribute('data-after-cutoff')) return false;
    return true;
  }

  #countPage() {
    try {
      const count = Number(window.sessionStorage.getItem(PAGES_KEY) || 0) + 1;
      window.sessionStorage.setItem(PAGES_KEY, String(count));
      return count;
    } catch {
      return 1;
    }
  }

  /** @param {boolean} on */
  #listen(on) {
    if (on === this.#listening) return;
    this.#listening = on;
    const method = on ? 'addEventListener' : 'removeEventListener';
    // Captured on the document, so it hears the window and `.page-wrapper` alike (scroll events do not bubble)
    document[method]('scroll', this.#onScroll, { passive: true, capture: true });
    for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
      document[method](type, this.#onInput, { passive: true, capture: true });
    }
    document[method]('mouseout', this.#onMouseOut);
    // Horizon's add to cart, quantity changes and the Luvè set buttons all announce themselves with this event
    document[method]('shopify:cart:lines-update', this.#onCart);
  }

  /** How far down the page the shopper has seen, in percent of the page */
  #onScroll = () => {
    const scroller = pageScroller();
    const height = Math.max(scroller.scrollHeight, 1);
    const depth = Math.min(100, ((scroller.scrollTop + scroller.clientHeight) / height) * 100);
    if (depth > this.#maxScroll) this.#maxScroll = depth;
  };

  #onInput = () => {
    this.#lastInput = performance.now();
  };

  /** Exit intent on a computer: the pointer leaves through the top of the window, towards the tabs */
  /** @param {MouseEvent} event */
  #onMouseOut = (event) => {
    if (!this.hasAttribute('data-exit-intent') || event.relatedTarget || event.clientY > 0) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (this.#visibleMs < EXIT_AFTER_MS) return;
    this.#tryOpen(true);
  };

  /** Something went into the cart: the shopper is buying, so this page asks nothing more */
  #onCart = () => {
    if (this.#opened) this.close('aside');
    this.#stopWaiting();
  };

  #tick = () => {
    const now = performance.now();
    if (document.visibilityState === 'visible') this.#visibleMs += now - this.#lastTick;
    this.#lastTick = now;
    if (this.#engaged()) this.#tryOpen(false);
  };

  #engaged() {
    if (this.#visibleMs < (Number(this.dataset.delay) || 25) * 1000) return false;
    this.#onScroll();
    return this.#maxScroll >= (Number(this.dataset.scroll) || 40) || this.#pages >= 2;
  }

  /** Something on the page needs the shopper more than a sign-up does */
  #inTheWay() {
    if (document.querySelector('dialog[open], details.menu-drawer-container[open]')) return true;
    if (document.querySelector('.luve-builder.is-in-view')) return true;
    const banner = document.getElementById('shopify-pc__banner');
    if (banner && banner.getClientRects().length && getComputedStyle(banner).visibility !== 'hidden') return true;
    return false;
  }

  /** @param {boolean} exitIntent - Leaving now: no need to wait for a pause */
  #tryOpen(exitIntent) {
    if (this.#opened || document.visibilityState !== 'visible' || this.#inTheWay()) return;
    if (!exitIntent) {
      if (performance.now() - this.#lastInput < QUIET_MS) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.matches('input, textarea, select, [contenteditable="true"]')) return;
    }
    this.open({ remember: true });
  }

  #stopWaiting() {
    clearInterval(this.#timer);
    this.#listen(false);
  }

  /** @param {{ remember?: boolean }} [options] - remember: count it as seen today */
  open({ remember = true } = {}) {
    if (this.#opened) return;
    this.#opened = true;
    this.#stopWaiting();
    if (remember) memory.write({ seenAt: Date.now() });

    const signup = this.#signup;
    signup?.applyOffer();
    const panel = this.querySelector('.luve-capture__panel');
    const headingId = signup?.headingId;
    if (panel && headingId) {
      panel.setAttribute('aria-labelledby', headingId);
      panel.removeAttribute('aria-label');
    }

    this.hidden = false;
    // Read the closed position once (a style flush), so the change below slides in. No animation frame is needed,
    // so it also opens where frames are paused
    void (/** @type {HTMLElement | null} */ (panel))?.offsetHeight;
    this.setAttribute('data-open', '');

    // A drawer, dialog or the set builder taking over the screen sends the panel away for this page
    this.#watch = setInterval(() => {
      if (this.#inTheWay()) this.close('aside');
    }, 700);
  }

  /** @param {'dismiss' | 'aside' | 'done'} reason */
  close(reason) {
    if (this.hidden) return;
    clearInterval(this.#watch);
    clearTimeout(this.#linger);
    if (reason === 'dismiss') memory.write({ dismissedAt: Date.now() });

    const hadFocus = this.contains(document.activeElement);
    this.removeAttribute('data-open');
    const finish = () => {
      this.hidden = true;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else setTimeout(finish, 450);
    // Focus goes back where it came from, not to a hidden panel
    if (hadFocus) {
      if (this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
      else if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
  }

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const from = event.relatedTarget;
    if (from instanceof HTMLElement && !this.contains(from)) this.#returnFocus = from;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-luve-capture-close]')) this.close('dismiss');
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close('dismiss');
    }
  };

  /** Signed up: the thanks takes focus, then the panel steps away, unless there is a code to copy or they are reading */
  #thank() {
    /** @type {HTMLElement | null} */ (this.querySelector('[data-luve-status]'))?.focus({ preventScroll: true });
    if (this.querySelector('[data-luve-status] [data-luve-copy]')) return;
    const leave = () => {
      if (this.matches(':hover, :focus-within') && !this.querySelector('[data-luve-status]:focus')) {
        this.#linger = setTimeout(leave, 2000);
        return;
      }
      this.close('done');
    };
    this.#linger = setTimeout(leave, SUCCESS_LINGER_MS);
  }
}

if (!customElements.get('luve-signup')) customElements.define('luve-signup', LuveSignup);
if (!customElements.get('luve-email-capture')) customElements.define('luve-email-capture', LuveEmailCapture);
